/**
 * AdminRTWShareCodeSubmission — admin banner for an applicant-provided RTW share code.
 *
 * Surfaces the pending Home Office share code an applicant submitted, so the admin
 * can (1) open the official GOV.UK Right to Work checking service, (2) reveal the
 * full code to run the check, and then record the outcome + upload evidence through
 * the existing "Record check" workflow. It never verifies Right to Work itself.
 *
 * Renders nothing unless there is a pending submission for this employee.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Shield, ExternalLink, Eye, EyeOff, Clock } from 'lucide-react';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

export default function AdminRTWShareCodeSubmission({ employeeId }) {
  const [submission, setSubmission] = useState(null);
  const [fullCode, setFullCode] = useState(null);
  const [revealing, setRevealing] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const load = useCallback(async () => {
    if (!employeeId || !token) return;
    try {
      const res = await fetch(`${API}/employees/${employeeId}/right-to-work/share-code-submission`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setSubmission(null); return; }
      const body = await res.json();
      setSubmission(body.submission || null);
    } catch (e) {
      setSubmission(null);
    }
  }, [employeeId, token]);

  useEffect(() => { load(); }, [load]);

  const handleReveal = async () => {
    if (fullCode) { setFullCode(null); return; } // toggle hide
    setRevealing(true);
    try {
      const res = await fetch(`${API}/employees/${employeeId}/right-to-work/share-code-submission/reveal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const body = await res.json();
      if (!res.ok) { toast.error(body.detail || 'Could not reveal share code'); return; }
      setFullCode(body.share_code);
    } catch (e) {
      toast.error('Could not reveal share code');
    } finally {
      setRevealing(false);
    }
  };

  if (!submission || submission.status !== 'pending_admin_review') return null;

  return (
    <div className="mb-4 rounded-2xl border border-amber-300 bg-amber-50 overflow-hidden" data-testid="admin-rtw-share-code-banner">
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="p-1.5 rounded-lg bg-amber-100 shrink-0">
          <Shield className="h-5 w-5 text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-700" />
            <p className="text-sm font-semibold text-amber-900">
              Applicant provided a Right to Work share code — online check required
            </p>
          </div>
          <p className="text-xs text-amber-800 mt-1">
            The applicant provided a code, not proof of work eligibility. Use the official GOV.UK
            employer checking service and retain its check result. <strong>Record Check</strong> below
            remains unavailable until the separate evidence review is accepted. Never approve Right
            to Work from the code alone.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-lg bg-white border border-amber-200 px-3 py-1.5 text-sm">
              <span className="text-xs text-amber-700">Share code:</span>
              <span className="font-mono font-semibold text-amber-900" data-testid="admin-rtw-share-code-value">
                {fullCode || submission.share_code_masked || '•••'}
              </span>
            </span>
            <button
              type="button"
              onClick={handleReveal}
              disabled={revealing}
              data-testid="admin-rtw-reveal-btn"
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-60"
            >
              {fullCode ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              {fullCode ? 'Hide code' : (revealing ? 'Revealing…' : 'Reveal code')}
            </button>
            <a
              href="https://www.gov.uk/view-right-to-work"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg bg-amber-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-800"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open GOV.UK Right to Work check
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
